import { ArrowDown, ArrowUp, ArrowUpDown, MoreHorizontal } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { CopyIdButton, MobileSortControl } from '@/components/shared';
import { useMediaQuery } from '@/lib/use-media-query';
import {
  formatDateTime,
  formatOwnerDisplayName,
  formatPricingTier,
  formatShortId,
} from '../format';
import type { BackofficeOrganizationRow, OrganizationSortBy, SortDirection } from '../types';

const SORTABLE_HEADERS: Record<OrganizationSortBy, string> = {
  name: 'Name',
  pricingTier: 'Pricing tier',
  updatedAt: 'Last updated',
  createdAt: 'Created',
};

const MOBILE_SORT_OPTIONS = [
  { value: 'name', label: SORTABLE_HEADERS.name },
  { value: 'pricingTier', label: SORTABLE_HEADERS.pricingTier },
  { value: 'updatedAt', label: SORTABLE_HEADERS.updatedAt },
  { value: 'createdAt', label: SORTABLE_HEADERS.createdAt },
] satisfies Array<{ value: OrganizationSortBy; label: string }>;

export interface OrganizationsTableProps {
  organizations: BackofficeOrganizationRow[];
  sortBy: OrganizationSortBy;
  sortDirection: SortDirection;
  onSortChange: (sortBy: OrganizationSortBy) => void;
}

function SortButton({
  label,
  field,
  sortBy,
  sortDirection,
  onSortChange,
}: {
  label: string;
  field: OrganizationSortBy;
  sortBy: OrganizationSortBy;
  sortDirection: SortDirection;
  onSortChange: (sortBy: OrganizationSortBy) => void;
}) {
  const active = field === sortBy;
  const Icon = active ? (sortDirection === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <button
      type="button"
      className="focus-ring inline-flex items-center gap-1 rounded-md text-left font-semibold text-neutral-600"
      aria-label={`Sort by ${label}`}
      onClick={() => onSortChange(field)}
    >
      <span>{label}</span>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}

export function OrganizationsTable({
  organizations,
  sortBy,
  sortDirection,
  onSortChange,
}: OrganizationsTableProps) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 767px)');

  if (isMobile) {
    return (
      <div>
        <MobileSortControl
          label="Sort organizations"
          options={MOBILE_SORT_OPTIONS}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
        />
        <div className="divide-y divide-neutral-100">
          {organizations.map((organization) => {
            const detailPath = `/organizations/${organization.id}`;

            return (
              <article
                key={organization.id}
                role="button"
                tabIndex={0}
                aria-label={`Open ${organization.name}`}
                className="cursor-pointer px-4 py-4 hover:bg-backoffice-50 focus:bg-backoffice-50 focus:outline-none"
                onClick={() => navigate(detailPath)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(detailPath);
                  }
                }}
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <h2 className="min-w-0 flex-1 text-body font-semibold text-neutral-900">
                    {organization.name}
                  </h2>
                  <span className="inline-flex shrink-0 rounded-full bg-neutral-100 px-2.5 py-1 text-small font-medium text-neutral-700">
                    {formatPricingTier(organization.pricingTier)}
                  </span>
                </div>
                <div className="mt-2 text-small text-neutral-600">
                  {organization.memberCount} {organization.memberCount === 1 ? 'member' : 'members'}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1320px] border-collapse text-left">
        <thead className="border-b border-neutral-200 bg-white text-small text-neutral-500">
          <tr>
            <th scope="col" className="w-12 px-4 py-3">
              <span className="sr-only">Select</span>
            </th>
            <th scope="col" className="min-w-[130px] px-4 py-3 font-semibold text-neutral-600">
              ID
            </th>
            <th scope="col" className="min-w-[240px] px-4 py-3">
              <SortButton
                label={SORTABLE_HEADERS.name}
                field="name"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="min-w-[190px] px-4 py-3 font-semibold text-neutral-600">
              Slug
            </th>
            <th scope="col" className="min-w-[160px] px-4 py-3">
              <SortButton
                label={SORTABLE_HEADERS.pricingTier}
                field="pricingTier"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="min-w-[220px] px-4 py-3 font-semibold text-neutral-600">
              Owner
            </th>
            <th scope="col" className="min-w-[150px] px-4 py-3 font-semibold text-neutral-600">
              Number members
            </th>
            <th scope="col" className="min-w-[180px] px-4 py-3">
              <SortButton
                label={SORTABLE_HEADERS.updatedAt}
                field="updatedAt"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="min-w-[160px] px-4 py-3">
              <SortButton
                label={SORTABLE_HEADERS.createdAt}
                field="createdAt"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="w-14 px-4 py-3">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 text-body">
          {organizations.map((organization) => {
            const ownerDisplayName = formatOwnerDisplayName(organization);
            const slugDisplay = organization.slug ?? 'No slug';
            const detailPath = `/organizations/${organization.id}`;

            return (
              <tr
                key={organization.id}
                className="cursor-pointer hover:bg-backoffice-50 focus-within:bg-backoffice-50"
                tabIndex={0}
                aria-label={`Open ${organization.name}`}
                onClick={() => navigate(detailPath)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(detailPath);
                  }
                }}
              >
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    aria-label={`Select ${organization.name}`}
                    className="h-4 w-4 rounded border-neutral-300 text-lavender-600"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  />
                </td>
                <td className="px-4 py-4 text-neutral-700">
                  <div className="group inline-flex items-center gap-2">
                    <span className="font-medium tabular-nums text-neutral-700">
                      {formatShortId(organization.id)}
                    </span>
                    <CopyIdButton value={organization.id} label="Copy organization ID" />
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="group inline-flex items-center gap-2">
                    <span className="font-medium text-neutral-900">{organization.name}</span>
                    <CopyIdButton value={organization.name} label="Copy organization name" />
                  </div>
                </td>
                <td className="px-4 py-4 text-neutral-700">
                  <div className="group inline-flex items-center gap-2">
                    <span>{slugDisplay}</span>
                    {organization.slug ? (
                      <CopyIdButton value={organization.slug} label="Copy slug" />
                    ) : null}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="inline-flex rounded-full bg-neutral-100 px-2.5 py-1 text-small font-medium text-neutral-700">
                    {formatPricingTier(organization.pricingTier)}
                  </span>
                </td>
                <td className="px-4 py-4 text-neutral-700">
                  <div className="group inline-flex items-center gap-2">
                    {organization.ownerUserId ? (
                      <Link
                        to={`/users/${organization.ownerUserId}`}
                        className="focus-ring rounded-sm font-medium text-neutral-900 hover:text-backoffice-600"
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        {ownerDisplayName}
                      </Link>
                    ) : (
                      <span>{ownerDisplayName}</span>
                    )}
                    <CopyIdButton value={ownerDisplayName} label="Copy owner name" />
                  </div>
                </td>
                <td className="px-4 py-4 tabular-nums text-neutral-700">
                  <Link
                    to={`/users?organizationId=${encodeURIComponent(organization.id)}`}
                    className="focus-ring rounded-sm font-medium text-neutral-900 hover:text-backoffice-600"
                    aria-label={`View members for ${organization.name}`}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    {organization.memberCount}
                  </Link>
                </td>
                <td className="px-4 py-4 text-neutral-700">
                  {formatDateTime(organization.updatedAt)}
                </td>
                <td className="px-4 py-4 text-neutral-700">
                  {formatDateTime(organization.createdAt)}
                </td>
                <td className="px-4 py-4">
                  <button
                    type="button"
                    className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 hover:bg-backoffice-50"
                    aria-label={`Actions for ${organization.name}`}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
